import { memo, useMemo } from 'react';
import { Grid, Divider, useEventCallback, SelectChangeEvent } from '@mui/material';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import { FormikErrors, FormikTouched } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import ListCategory from 'practice/components/shared/list-category';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import AddressAutoComplete from 'system/components/shared/search-address';
import StreetNumberList, { StreetNumberVM } from 'system/components/shared/street-number-list';
import { DatePicker } from '@mui/lab';
import ListHouseOccupation from 'practice/components/shared/list-house-occupation';
import { transformExponent } from 'system/components/shared/street-number-list/transform-exponent';
import { ItalianVM } from 'shell/view-models/register-office-practice-vm';

interface FormProps {
  values?: ItalianVM;
  onChange: (key: keyof ItalianVM, value: ItalianVM[keyof ItalianVM]) => void;
  onChanges: (values: ItalianVM) => void;
  errors?: FormikErrors<ItalianVM>;
  touched?: FormikTouched<ItalianVM>;
  disabled?: boolean;
}

const ItalianFormComponent = ({
  values,
  onChange,
  onChanges,
  errors,
  touched,
  disabled,
}: FormProps): React.ReactElement => {
  const onChangeCategory = useEventCallback((cat: SelectChangeEvent<string>) => {
    onChange('cat', cat.target.value);
  });

  const onChangeMunicipalityList = useEventCallback((municipality: MunicipalityAnprVM | null) => {
    onChange('municipality', municipality);
  });

  const onChangeStreetNumberList = useEventCallback((item: StreetNumberVM | null) => {
    onChanges({
      number: item?.id ? Number.parseInt(item.id?.toString(), 10) : undefined,
      cap: item?.cap || '',
      exponent: item?.exponent,
    });
  });

  const onChangeHouseOccupation = useEventCallback((value: string) => {
    onChanges({
      houseOccupationType: value,
      sub: '',
      section: '',
      particle: '',
      sheet: '',
      note: '',
      cat: '',
      numberContract: '',
      dateContract: null,
      officeContract: null,
    });
  });

  const isRequiredCadastral = useMemo(() => {
    if (
      values?.section?.toUpperCase() === 'NC' ||
      values?.section?.toUpperCase() === 'NP' ||
      values?.houseOccupationType !== '1'
    ) {
      return false;
    }
    return true;
  }, [values?.section, values?.houseOccupationType]);

  const isRequiredControlRevenueAgency = useMemo(() => {
    if (values?.houseOccupationType !== '2' && values?.houseOccupationType !== '4') {
      return false;
    }
    return true;
  }, [values?.houseOccupationType]);

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <PmiTitleDivider>Indirizzo</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={8}>
        <MunicipalityList
          label="Comune"
          required
          value={values?.municipality || null}
          onChange={(municipality) => onChangeMunicipalityList(municipality)}
          id="municipality"
          error={touched?.municipality && Boolean(errors?.municipality)}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          label="CAP *"
          aria-label="cap"
          id="cap"
          value={values?.cap || ''}
          onChange={(event) => {
            onChange('cap', event.target.value);
          }}
          fullWidth
          error={touched?.cap && Boolean(errors?.cap)}
          helperText={errors?.cap}
        />
      </Grid>
      <Grid item xs={12} md={9}>
        <AddressAutoComplete
          selectedValue={values?.addressAuto || null}
          handleChange={(value) => {
            onChange('addressAuto', value);
          }}
          error={touched?.addressAuto && Boolean(errors?.addressAuto)}
        />
      </Grid>
      <Grid item xs={12} md={3}>
        <StreetNumberList
          label="Numero *"
          onChange={onChangeStreetNumberList}
          noEnded
          valueStreetNumber={
            values?.number
              ? {
                  id: values?.number,
                  value: `${values?.number?.toString()}${transformExponent(
                    values?.exponent || ''
                  )}`,
                }
              : null
          }
          showExponent
          disabled={disabled || !values?.addressAuto?.code}
          circulationArea={values?.addressAuto?.code?.toString() || ''}
          error={touched?.number && Boolean(errors?.number)}
          helperText={errors?.number}
        />
      </Grid>
      <Grid item xs={12} md={8}>
        <ListHouseOccupation
          error={touched?.houseOccupationType && Boolean(errors?.houseOccupationType)}
          value={values?.houseOccupationType || ''}
          onChange={onChangeHouseOccupation}
          disabled={disabled}
        />
      </Grid>

      {values?.houseOccupationType === '2' || values?.houseOccupationType === '4' ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Controllo Agenzia Entrate</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={6}>
            <PmiTextField
              id="officeContract"
              label={!isRequiredControlRevenueAgency ? 'Sede' : 'Sede *'}
              aria-label="Sede"
              fullWidth
              helperText={errors?.officeContract}
              error={touched?.officeContract && Boolean(errors?.officeContract)}
              value={values?.officeContract || ''}
              onChange={(event) => onChange('officeContract', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="numberContract"
              label={!isRequiredControlRevenueAgency ? 'Numero' : 'Numero *'}
              aria-label="Numero"
              fullWidth
              helperText={errors?.numberContract}
              error={touched?.numberContract && Boolean(errors?.numberContract)}
              value={values?.numberContract || ''}
              onChange={(event) => onChange('numberContract', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label={!isRequiredControlRevenueAgency ? 'Data' : 'Data *'}
              aria-label="Data"
              value={values?.dateContract || null}
              onChange={(value) => onChange('dateContract', value)}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="dateContract"
                  helperText={errors?.dateContract}
                  error={touched?.dateContract && Boolean(errors?.dateContract)}
                  fullWidth
                />
              )}
            />
          </Grid>
        </>
      ) : undefined}
      {values?.houseOccupationType ? (
        <>
          <Grid item xs={12}>
            <PmiTitleDivider>Dati catastali</PmiTitleDivider>
          </Grid>
          <Grid item xs={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="section"
              label="Sezione"
              aria-label="Sezione"
              fullWidth
              helperText={errors?.section}
              error={touched?.section && Boolean(errors?.section)}
              value={values?.section?.toUpperCase() || ''}
              onChange={(event) => onChange('section', event.target.value?.toUpperCase())}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="sheet"
              label={!isRequiredCadastral ? 'Foglio' : 'Foglio *'}
              aria-label="Foglio"
              fullWidth
              type="number"
              helperText={errors?.sheet}
              error={touched?.sheet && Boolean(errors?.sheet)}
              value={values?.sheet || ''}
              onChange={(event) => onChange('sheet', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="particle"
              label={!isRequiredCadastral ? 'Particella' : 'Particella *'}
              aria-label="Particella"
              fullWidth
              type="number"
              helperText={errors?.particle}
              error={touched?.particle && Boolean(errors?.particle)}
              value={values?.particle || ''}
              onChange={(event) => onChange('particle', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="sub"
              label={!isRequiredCadastral ? 'Subalterno' : 'Subalterno *'}
              aria-label="Subalterno"
              fullWidth
              type="number"
              helperText={errors?.sub}
              error={touched?.sub && Boolean(errors?.sub)}
              value={values?.sub || ''}
              onChange={(event) => onChange('sub', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ListCategory
              value={values?.cat || ''}
              error={touched?.cat && Boolean(errors?.cat)}
              onChange={onChangeCategory}
            />
          </Grid>
        </>
      ) : undefined}
      {values?.houseOccupationType !== '1' &&
      values?.houseOccupationType !== '2' &&
      values?.houseOccupationType !== '4' ? (
        <Grid item xs={12}>
          <PmiTextField
            aria-label="Note"
            label="Note"
            id="note"
            name="note"
            fullWidth
            type="text"
            variant="outlined"
            multiline
            rows={5}
            value={values?.note || ''}
            onChange={(event) => onChange('note', event.target.value)}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

ItalianFormComponent.displayName = 'Accordion New Residence Form - Italian Form';

const ItalianForm = memo(ItalianFormComponent);

export default ItalianForm;
